/**
 * Print-only design tokens for the Monthly Consultation PDF (Canva redesign).
 * Single source of truth for colors, spacing, typography, and the dot-grid
 * background used by print atoms and chapters.
 *
 * Aesthetic reference: Canva monthly-report chrome — inner cream page,
 * terracotta eyebrow, tan-gold accents, navy charcoal type, Georgia display.
 * Existing export names are stable so chapters keep importing them.
 */

/**
 * Color palette — Canva redesign tokens.
 * Use these for all PDF ink; do not hardcode hex values in atoms or chapters.
 */
/** Inner cream page field (Canva redesign). */
export const cream = "#fdfbf7";
/** Primary text and navy chrome. */
export const charcoal = "#1a1e3f";
/** Eyebrow labels and strong negative accents (legacy red). */
export const deepRed = "#b91c1c";
/**
 * Terracotta accent for eyebrows, status labels, and warm chrome.
 * Canonical Canva terracotta — use this token everywhere that color is needed.
 */
export const terracotta = "#c2410c";
/**
 * Bright cover-bar orange (Canva page-01 MONTHLY / brand gradient endpoint).
 * Pair with `deepRed` via `coverBarGradient` — not a general UI accent.
 */
export const coverOrange = "#f7701e";
/**
 * Cover chrome horizontal gradient: deep red → bright orange (Canva page-01).
 */
export const coverBarGradient = `linear-gradient(90deg, ${deepRed} 0%, ${coverOrange} 100%)`;
/** Warm secondary accent (legacy gold). */
export const gold = "#a16207";
/** Tan-gold accent for Growing status, pills, and secondary highlights. */
export const tanGold = "#c9873a";
/** Positive / constructive accent. */
export const green = "#15803d";
/**
 * Week-calendar Steady header bar (Canva Weeks “STEADY WORK”).
 * Distinct from terracotta / gold so the 2×2 week grid stays scannable.
 */
export const steadyBlue = "#2563eb";
/** Alert / pink-red accent (score dips, warnings). */
export const pinkRed = "#dc2626";
/** Si Hua 化禄 — resource / Lu (green). Matches web `SI_HUA_COLOR`. */
export const siHuaLu = "#16a34a";
/** Si Hua 化权 — support / Quan (blue). Matches web `SI_HUA_COLOR`. */
export const siHuaQuan = steadyBlue;
/** Si Hua 化科 — recognition / Ke (yellow-gold). Matches web `SI_HUA_COLOR`. */
export const siHuaKe = "#ca8a04";
/** Si Hua 化忌 — lead / Ji (red). Matches web `SI_HUA_COLOR`. */
export const siHuaJi = pinkRed;
/** Pale sage tint for print Do cards (Canva Career / Wealth / Relationship). */
export const paleGreen = "#eef5ee";
/** Pale pink tint for print Avoid cards (Canva Career / Wealth / Relationship). */
export const palePink = "#f8ecec";
/** Light tan / beige fill for Canva personal-note cards (Letter, not palePink). */
export const paleTan = "#f3eadc";
/** Secondary labels and muted body copy. */
export const mutedGray = "#6b7280";
/** Chapter card and pill surfaces. */
export const cardWhite = "#ffffff";
/** Card and divider strokes. */
export const border = "#e8ddd0";
/**
 * Dark callout / insight-bar fill (Canva navy panel).
 * Prefer this over charcoal when the surface is a filled bar, not text.
 */
export const insightDark = "#1a1e3f";

/**
 * Spacing — card geometry and A4 page chrome padding (px).
 * `@page` margin is 0; these paddings provide the printable inset.
 */
/** Chapter card corner radius. */
export const cardRadius = 16;
/** Inner padding inside a white chapter card. */
export const cardPadding = 20;
/** Horizontal inset from the A4 page edge to content. */
export const pagePaddingX = 36;
/** Vertical inset from the A4 page edge to content. */
export const pagePaddingY = 40;

/**
 * Elevation — soft shadow for white cards on the cream / dot-grid page.
 */
export const cardShadow = "0 2px 8px rgba(26, 30, 63, 0.06), 0 1px 2px rgba(26, 30, 63, 0.04)";

/**
 * Typography — existing fonts only (no new files, no Google Fonts).
 * Display = Georgia for titles; body = system sans for labels and prose.
 * Cover condensed stack is reserved for poster-style cover type (later phases).
 */
/** Serif stack for chapter titles and display headings. */
export const displayFontFamily = "Georgia, \"Times New Roman\", serif";
/** System sans stack for body copy, labels, and chrome. */
export const bodyFontFamily =
  "-apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, sans-serif";
/**
 * Condensed display stack for cover poster type.
 * Arial Narrow first so `fontWeight` can lighten; Impact only if Narrow is absent.
 */
export const coverCondensedFontFamily =
  "\"Arial Narrow\", \"Helvetica Narrow\", Impact, sans-serif";

/**
 * Dot-grid page background — cream field with a light radial-dot pattern.
 * Spread onto a page/chapter container via `style={{ ...dotGridStyle }}`,
 * or reuse `dotGridBackgroundImage` / `dotGridBackgroundSize` in CSS strings.
 */
export const dotGridBackgroundImage =
  "radial-gradient(circle, rgba(26, 30, 63, 0.07) 1px, transparent 1px)";
/** Dot pitch (horizontal × vertical). */
export const dotGridBackgroundSize = "14px 14px";

/**
 * Ready-to-spread React inline style for the Canva cream + dot-grid page.
 */
export const dotGridStyle: {
  readonly backgroundColor: string;
  readonly backgroundImage: string;
  readonly backgroundSize: string;
} = {
  backgroundColor: cream,
  backgroundImage: dotGridBackgroundImage,
  backgroundSize: dotGridBackgroundSize,
};

/**
 * CSS declaration fragment for the same pattern (injectable into a stylesheet).
 */
export const dotGridCss = [
  `background-color: ${cream};`,
  `background-image: ${dotGridBackgroundImage};`,
  `background-size: ${dotGridBackgroundSize};`,
].join(" ");
